import { Form, Link, useActionData, useLoaderData, useNavigation, useParams, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { Component, ComponentLocation, ComponentMemory, Constraint, Decision } from "@edm/contracts";
import { barLength, KeyValueSkeleton, Loading, TableSkeleton } from "../../components/skeletons.jsx";
import { Deferred } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Badge, Button, Card, FormError, KeyValue, PageHeader, Skeleton, StatusBadge, Table, TextField } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

// Components (ENG-13): logical architecture surfaces, their source-location
// mappings with provenance, and the decisions and constraints scoped to them.

// Component has no list page and no sidebar entry. It is met inside a
// finding, a cascade, or an answer — this file is the detail page those land on.

// The generated ComponentMemory leaves $ref'd members as open maps; narrow them here.
type ComponentView = Omit<ComponentMemory, "component"> & {
  component: Component;
  locations: ComponentLocation[];
  activeDecisions: Decision[];
  pendingDecisions: Decision[];
  historicalDecisions: Decision[];
  constraints: Constraint[];
};

export function componentLoader({ context, params }: LoaderFunctionArgs) {
  const view = context.get(apiContext).workspace(context.get(workspaceContext).id).componentMemory(params.id!) as unknown as Promise<ComponentView>;
  return { view: deferred(view) };
}

export async function componentAction({ context, params, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const form = await request.formData();
  const field = (k: string) => String(form.get(k) ?? "").trim();
  try {
    if (form.get("intent") === "add-location") {
      await api.addComponentLocation(params.id!, { pattern: field("pattern"), ...(field("repository") ? { repository: field("repository") } : {}) });
    } else {
      await api.resolveLocation(field("locationId"), form.get("intent") === "confirm");
    }
    return { ok: true };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function DecisionRows({ title, items, base }: { title: string; items: Decision[]; base: string }) {
  if (items.length === 0) return null;
  return (
    <Card title={title} padded={false}>
      <Table>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td className="dt-nowrap">
                <Link to={`${base}/decisions/${d.id}`} className="dt-mono">
                  {d.id}
                </Link>
              </td>
              <td className="dt-table__primary">{d.title}</td>
              <td className="dt-nowrap">
                <StatusBadge status={d.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function LocationRow({ l, busy }: { l: ComponentLocation; busy: boolean }) {
  const { t, tOr } = useI18n();
  const reviewable = l.status === "active" && l.provenance !== "human_confirmed";
  return (
    <tr data-testid="location">
      <td className="dt-mono">{l.pattern}</td>
      <td className="dt-subtle">{l.repository || t("component.locations.anyRepository")}</td>
      <td>
        <Badge tone={l.provenance === "human_confirmed" ? "success" : l.provenance === "imported" ? "neutral" : "info"}>
          {tOr(`provenance.${l.provenance}`, l.provenance.replace("_", " "))}
        </Badge>{" "}
        <span className="dt-subtle">{Math.round(l.confidence * 100)}%</span>
      </td>
      <td>
        <StatusBadge status={l.status === "active" ? "active" : "rejected"} />
      </td>
      <td className="dt-nowrap">
        {reviewable && (
          <span className="dt-inline dt-gap-2">
            {(["reject", "confirm"] as const).map((intent) => (
              <Form method="post" key={intent}>
                <input type="hidden" name="intent" value={intent} />
                <input type="hidden" name="locationId" value={l.id} />
                <Button type="submit" size="sm" variant={intent === "confirm" ? "primary" : "ghost"} loading={busy}>
                  {intent === "confirm" ? t("component.locations.confirm") : t("component.locations.reject")}
                </Button>
              </Form>
            ))}
          </span>
        )}
      </td>
    </tr>
  );
}

/** The component's facts, then where it lives in the code. The id is known from the URL. */
export function ComponentSkeleton() {
  const { t } = useI18n();
  const { id } = useParams();
  return (
    <Loading>
      <PageHeader eyebrow={<code>{id}</code>} title={<Skeleton width={240} />} description={<Skeleton width={360} />} />
      <div className="dt-article">
        <Card>
          <KeyValueSkeleton keys={[t("component.kv.kind"), t("component.kv.status"), t("component.kv.aliases"), t("component.kv.keys"), t("component.kv.origin")]} />
        </Card>
        <Card title={t("component.locations.title")} description={t("component.locations.description")} padded={false}>
          <TableSkeleton
            count={3}
            cells={(i) => [<Skeleton width={barLength(i)} />, <Skeleton width={96} />, <Skeleton shape="badge" width={96} />, <Skeleton shape="badge" width={64} />, <Skeleton shape="button-sm" />]}
          />
        </Card>
      </div>
    </Loading>
  );
}

export function ComponentRoute() {
  const { view } = useLoaderData<typeof componentLoader>();
  return (
    <Deferred data={view} fallback={<ComponentSkeleton />}>
      {(view) => <ComponentPage view={view} />}
    </Deferred>
  );
}

function ComponentPage({ view }: { view: ComponentView }) {
  const result = useActionData() as { error?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t, tOr } = useI18n();
  const base = `/w/${workspace.slug}`;
  const c = view.component;
  return (
    <>
      <PageHeader
        eyebrow={<code>{c.id}</code>}
        title={c.name}
        description={c.description}
      />
      <div className="dt-article">
        <FormError message={result?.error} />
        <Card>
          <KeyValue
            items={[
              { key: t("component.kv.kind"), value: tOr(`componentKind.${c.kind}`, c.kind) },
              { key: t("component.kv.status"), value: tOr(`status.${c.status}`, c.status) },
              { key: t("component.kv.aliases"), value: c.aliases.join(", ") || t("common.none") },
              { key: t("component.kv.keys"), value: <span className="dt-mono">{c.keys?.join(", ") || t("common.none")}</span> },
              { key: t("component.kv.origin"), value: tOr(`origin.${c.origin}`, c.origin) },
            ]}
          />
        </Card>
        <Card title={t("component.locations.title")} description={t("component.locations.description")} padded={false}>
          <Table>
            <tbody>
              {view.locations.map((l) => (
                <LocationRow key={l.id} l={l} busy={busy} />
              ))}
            </tbody>
          </Table>
          <Form method="post" className="dt-inline dt-gap-2" style={{ padding: "var(--dt-space-4)", flexWrap: "wrap", alignItems: "end" }}>
            <input type="hidden" name="intent" value="add-location" />
            <TextField name="pattern" label={t("component.locations.path")} placeholder={t("component.locations.pathPlaceholder")} required />
            <TextField name="repository" label={t("component.locations.repository")} placeholder={t("component.locations.anyRepository")} />
            <Button type="submit" variant="secondary" loading={busy}>
              {t("component.locations.map")}
            </Button>
          </Form>
        </Card>
        <DecisionRows title={t("component.activeDecisions")} items={view.activeDecisions} base={base} />
        <DecisionRows title={t("component.pendingDecisions")} items={view.pendingDecisions} base={base} />
        <DecisionRows title={t("component.historicalDecisions")} items={view.historicalDecisions} base={base} />
        {view.constraints.length > 0 && (
          <Card title={t("component.constraints")} padded={false}>
            <Table>
              <tbody>
                {view.constraints.map((k) => (
                  <tr key={k.id}>
                    <td className="dt-mono dt-nowrap">{k.id}</td>
                    <td>{k.statement}</td>
                    <td className="dt-nowrap">
                      <StatusBadge status={k.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </div>
    </>
  );
}
